<template>
  <div v-if="recommandation && data.length">
    <div class="level mb-10">
      <div class="level-left">
        <h2 class="is-size-14px has-text-grid-1 has-text-weight-bold">
          {{ titre }}
        </h2>
      </div>
    </div>
    <b-table
      :data="data"
      @click="modifierPanneau"
      hoverable>
      <b-table-column
        v-for="column in columns"
        :key="column.id"
        v-bind="column"
        header-class="is-size-7 has-text-grid-1 has-text-middle has-text-weight-bold">
        <template v-slot="{ row }">
          <span v-if="column.number">
            {{ func.formatNombre(row.budget[column.field]) }}
          </span>
          <span v-else>
            {{ formatContent(row, column) }}
          </span>
          <b-tooltip
            v-if="showIconErrorPanneau(row, column)"
            :label="$t('devis.errors.productWithError')"
            position="is-right"
            type="is-danger">
            <b-icon
              icon="alert-circle-outline"
              type="is-danger"
              size="is-small">
            </b-icon>
          </b-tooltip>
        </template>
      </b-table-column>
      <template slot="footer">
        <td v-for="n in (columns.length - columnFooter.length)" :key="n"></td>
        <td
          v-for="(column, i) in columnFooter"
          :key="'columnFooter' + i"
          class="is-size-7
            has-text-black
            has-text-middle
            has-text-right
            has-text-weight-bold
            py-15">
          {{ func.formatNombre(column.value) }}
        </td>
      </template>
    </b-table>
  </div>
</template>

<script>
import get from 'lodash/get';
import isEmpty from 'lodash/isEmpty';
import moment from 'moment';
import { isValidPanneau } from '@/services/gestionPanneau/validationPanneau';
import { formatNombre, adressFullStringGetter } from '@/services/general/general';
import { LISTE_CATEGORIES_HONORAIRE } from '@/configuration/configPanneau';
import { GUID_VIDE } from '@/configuration/general';
import { genererPanneauHonoraireForfaitaire } from '@/services/recommandation/gestionHonoraireFortaitaire';

const COLUMN_INFO = ['ancienPanneau', 'referencePanneau', 'regie', 'format', 'materiel', 'categorie', 'date', 'adresse'];
const COLUMN_BUDGET = ['prixBrutNegocie', 'prixNet', 'tlpe', 'honoraires', 'honoraireFraisTraitement', 'fraisTechnique', 'prixTotal'];

export default {
  props: {
    panneaux: Array,
    societeId: String,
    recommandation: Object,
    honoraireForfaitaire: Number,
  },
  data() {
    return {
      func: { formatNombre },
    };
  },
  computed: {
    totalPrix() {
      return COLUMN_BUDGET.reduce((acc, key) => {
        acc[key] = this.calculTotalPrix(key);
        return acc;
      }, {});
    },
    columnFooter() {
      return COLUMN_BUDGET.map(field => ({
        label: 'total' + this.strUcFirst(field),
        value: this.totalPrix[field],
      }));
    },
    colonneLeft() {
      return COLUMN_INFO.reduce((acc, key) => {
        acc[key] = this.data.some(el => !isEmpty(el[key]));
        return acc;
      }, {});
    },
    nrbColonneLeft() {
      return Object.values(this.colonneLeft).filter(el => el).length;
    },
    titre() {
      return this.$t('produitsPanier.libelleTabBudgetAnnuel');
    },
    data() {
      const lignes = [];
      this.panneaux.filter(el => el.actif).forEach(panneau => {
        (panneau.budgets || []).forEach(budget => {
          lignes.push({
            ...panneau,
            budget,
            index: lignes.length + 1,
            error: !isValidPanneau(panneau) && !panneau.estPanneauSousContrat,
            date: this.calcPeriode(budget),
            categorie: panneau.categorie ? panneau.categorie.libelle : '',
            isEcheancierFictif: !!panneau.echeanciers && panneau.echeanciers.some(obj => obj.id === GUID_VIDE),
            ancienPanneau: (panneau.estPanneauSousContrat || panneau.estRenouvellement) ? 'OUI' : 'NON',
          });
        });
      });
      if (this.honoraireForfaitaire) {
        const panneauHF = genererPanneauHonoraireForfaitaire(this.honoraireForfaitaire);
        const budget = panneauHF.budgets && panneauHF.budgets[0] ? panneauHF.budgets[0] : panneauHF.budgetGeneration;
        lignes.push({
          ...panneauHF,
          budget,
          error: false,
          index: lignes.length + 1,
          date: this.calcPeriode(budget),
          categorie: panneauHF.categorie ? panneauHF.categorie.libelle : '',
          isEcheancierFictif: false,
          isHonoraireForfaitaire: true,
        });
      }
      return lignes;
    },
    columns() {
      return [
        this.getColumnObjet('index', 40, this.colonneLeft),
        this.getColumnObjet('ancienPanneau', 80, this.colonneLeft),
        this.getColumnObjet('referencePanneau', 100, this.colonneLeft),
        this.getColumnObjet('regie', 120, this.colonneLeft),
        this.getColumnObjet('format', 100, this.colonneLeft),
        this.getColumnObjet('materiel', 100, this.colonneLeft),
        this.getColumnObjet('categorie', 100, this.colonneLeft),
        this.getColumnObjet('date', 100, this.colonneLeft),
        this.getColumnObjet('adresse', 120, this.colonneLeft),
        this.getColumnObjet('prixBrutNegocie', 100, this.totalPrix, false),
        this.getColumnObjet('prixNet', 80, this.totalPrix, false),
        this.getColumnObjet('tlpe', 80, this.totalPrix, false),
        this.getColumnObjet('honoraires', 80, this.totalPrix, false),
        this.getColumnObjet('honoraireFraisTraitement', 80, this.totalPrix, false),
        this.getColumnObjet('fraisTechnique', 80, this.totalPrix, false),
        this.getColumnObjet('prixTotal', 80, this.totalPrix, false),
      ];
    },
  },
  methods: {
    getColumnObjet(field, width, objToCheck, isLeft = true) {
      let fieldValue = objToCheck[field] ? field : '';
      if (field === 'index') fieldValue = 'index';
      let cellClass = isLeft
        ? 'is-size-7 has-text-default has-text-middle'
        : 'is-size-7 has-text-default has-text-middle has-text-right'
      if (field === 'prixTotal') {
        cellClass = 'is-size-7 has-text-black has-text-middle has-text-right has-text-weight-bold';
      }
      return {
        width,
        field: fieldValue,
        label: this.$t(`produitsPanier.tabBudget.${field}`),
        'cell-class': cellClass,
        numeric: !isLeft,
        number: !isLeft,
      };
    },
    calcPeriode(budget) {
      const debut = budget.dateDebut ? moment(budget.dateDebut).format('L') : '';
      const fin = budget.dateFin ? moment(budget.dateFin).format('L') : '';
      if (debut && fin) return this.$t('general.periodeDuAu', { debut, fin });
      return '';
    },
    strUcFirst(a) {
      return (a + '').charAt(0).toUpperCase() + a.substr(1);
    },
    modifierPanneau(panneau) {
      const { guid, estPanneauSousContrat, isHonoraireForfaitaire } = panneau;
      let nameRoute = estPanneauSousContrat ? 'view-panneau' : 'modifier-panneau';
			if (isHonoraireForfaitaire) {
				nameRoute = 'liste-panneau';
			}
      this.$router.push({ name: nameRoute, params: { guid } });
    },
    formatContent(data, column) {
      switch(column.field) {
        case 'adresse':
          return adressFullStringGetter(data.adresse);
        case 'format':
          return LISTE_CATEGORIES_HONORAIRE.includes(data.categorie) ? '' : data[column.field];
        default:
          return data[column.field];
      }
    },
    calculTotalPrix(field) {
      if (!this.data.length) return 0;
      return this.data
        .map(el => get(el.budget, field, 0))
        .reduce((a, b) => a + b);
    },
    showIconErrorPanneau(pan, column) {
      return pan.error && column.field === 'index';
    }
  },
};
</script>
